import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type {
  ClientMatrixCommandResponse,
  ClientMatrixEntry,
  ClientMatrixStatus,
  CreateClientMatrixRequest,
  EvidenceClass,
  ReinstateClientMatrixRequest,
  ReinstateClientMatrixResponse,
  RevalidateClientMatrixRequest,
  SetClientMatrixStatusRequest,
} from "../../api/staffTypes";
import { Banner, Button, Card, EmptyState, Input, PageHeader, QueryView, Select, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import { AwaitingSecondBanner, Mono, NotAppliedBanner, parseEvidence, reasonError, shortId, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// The approved client matrix (ARCHITECTURE.md §3; migrations 0047, 0048,
// 0069; client_matrix.go): which MCP client versions paid inventory is
// served to. Every row is a Phase-0-validated combination, so creating one
// is validating it and needs evidence. Status changes go through the
// suspension machinery; safety_suspended is a full safety finding for the
// version. Every write needs a recent step-up; refusals are 409
// request_rejected with the SQL's words. Migration 0069 (UX-DESIGN.md §5's
// tighten/loosen asymmetry): suspending stays single-reviewer and
// immediate through /status; reinstating (status:"validated") is refused
// there (409 reinstatement_requires_dual_control) and needs a second,
// distinct reviewer through the dedicated /reinstate route below.

const STATUS_TONE: Record<ClientMatrixStatus, "success" | "warning" | "error"> = {
  validated: "success",
  ordinary_suspended: "warning",
  safety_suspended: "error",
};

const CLASS_OPTIONS = [
  { value: "independently_verified", label: "Independently verified" },
  { value: "publisher_attested", label: "Publisher attested" },
];

function refreshMatrix(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: staffKeys.clientMatrix });
}

function CommandOutcome({ res }: { res: ClientMatrixCommandResponse }) {
  if (!res.applied) return <NotAppliedBanner result={res.result}>The entry is already {res.status.replace(/_/g, " ")}.</NotAppliedBanner>;
  return (
    <Banner tone="success" live="polite" title={`Recorded: ${res.result.replace(/_/g, " ")}`}>
      Status {res.status.replace(/_/g, " ")}
      {res.status_before ? ` (was ${res.status_before.replace(/_/g, " ")})` : ""}
      {res.held?.length ? `. Held now: ${res.held.map((h) => h.replace(/_/g, " ")).join(", ")}` : ""}.
    </Banner>
  );
}

export function ClientMatrixPage() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: staffKeys.clientMatrix, queryFn: () => ep.staff.clientMatrix.list() });
  return (
    <div className="stack-lg">
      <PageHeader title="Approved client matrix" description="MCP client versions validated for paid inventory." />
      <QueryView query={q} loadingLabel="Loading the client matrix">
        {(data) => (
          <Table
            caption="Client versions"
            rows={data.entries}
            rowKey={(e) => e.approved_client_matrix_id}
            empty={<EmptyState title="No entries">No client version is approved yet.</EmptyState>}
            columns={[
              { key: "client", header: "Client", cell: (e) => <Link to={`/staff/client-matrix/${e.approved_client_matrix_id}`}>{`${e.client_name} ${e.version}`}</Link> },
              { key: "status", header: "Status", cell: (e) => <StatusPill tone={STATUS_TONE[e.status]}>{e.status.replace(/_/g, " ")}</StatusPill> },
              { key: "class", header: "Evidence class", cell: (e) => e.evidence_class.replace(/_/g, " ") },
              { key: "susp", header: "Active suspensions", numeric: true, cell: (e) => e.active_suspensions.length },
              { key: "scope", header: "Scope version", numeric: true, cell: (e) => e.scope_version ?? "—" },
              { key: "created", header: "Created", cell: (e) => when(e.created_at) },
            ]}
          />
        )}
      </QueryView>
      <CreateEntryCard />
    </div>
  );
}

function CreateEntryCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [clientName, setClientName] = useState("");
  const [version, setVersion] = useState("");
  const [evidenceClass, setEvidenceClass] = useState<EvidenceClass | "">("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand((req: CreateClientMatrixRequest, opts) => ep.staff.clientMatrix.create(req, opts), () => refreshMatrix(qc));
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!clientName.trim() || !version.trim()) return setFormError("Enter the client name and version.");
    if (!evidenceClass) return setFormError("Choose an evidence class.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const ev = parseEvidence(evidence, { required: true });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    cmd.run({ client_name: clientName.trim(), version: version.trim(), evidence_class: evidenceClass, reason: reason.trim(), evidence: ev.value });
  }
  return (
    <Card title="Add a validated client version" subtitle="Creating an entry is validating it: Phase-0 evidence is required.">
      {cmd.data ? <CommandOutcome res={cmd.data} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <div className="grid-2">
          <Input label="Client name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
          <Input label="Version" mono value={version} onChange={(e) => setVersion(e.target.value)} />
        </div>
        <Select label="Evidence class" value={evidenceClass} placeholder="Choose…" options={CLASS_OPTIONS} onChange={(e) => setEvidenceClass(e.target.value as EvidenceClass)} />
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <Textarea label="Evidence (JSON object)" className="mono" rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} hint='Required, e.g. {"phase0_run": "2026-09-20", "report": "…"}.' />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" loading={cmd.isPending}>
            Add entry
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function ClientMatrixEntryPage() {
  const { entryId = "" } = useParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: staffKeys.clientMatrix, queryFn: () => ep.staff.clientMatrix.list() });
  return (
    <div className="stack-lg">
      <PageHeader title={`Client version ${shortId(entryId)}`} back={{ to: "/staff/client-matrix", label: "Client matrix" }} />
      <QueryView query={q} loadingLabel="Loading the entry">
        {(data) => {
          const entry = data.entries.find((e) => e.approved_client_matrix_id === entryId);
          if (!entry) return <Banner tone="warning" title="Not found">There's no client matrix entry with that id.</Banner>;
          return <EntryDetail entry={entry} />;
        }}
      </QueryView>
    </div>
  );
}

function EntryDetail({ entry }: { entry: ClientMatrixEntry }) {
  return (
    <div className="stack">
      <Card title={`${entry.client_name} ${entry.version}`} actions={<StatusPill tone={STATUS_TONE[entry.status]}>{entry.status.replace(/_/g, " ")}</StatusPill>}>
        <dl className="definition-list">
          <dt>Evidence class</dt>
          <dd>{entry.evidence_class.replace(/_/g, " ")}</dd>
          <dt>Scope version</dt>
          <dd>{entry.scope_version ?? "—"}</dd>
          <dt>Active suspensions</dt>
          <dd>
            {entry.active_suspensions.length
              ? entry.active_suspensions.map((s) => (
                  <div key={s.suspension_id}>
                    {s.kind} · <Mono>{shortId(s.suspension_id)}</Mono> · from {when(s.effective_at)} · {s.reason}
                  </div>
                ))
              : "None"}
          </dd>
        </dl>
      </Card>
      <Card title="History">
        <Table
          caption="Every change to this entry"
          captionHidden
          rows={entry.events}
          rowKey={(e) => e.event_id}
          columns={[
            { key: "at", header: "When", cell: (e) => when(e.occurred_at) },
            { key: "action", header: "Action", cell: (e) => e.action.replace(/_/g, " ") },
            { key: "source", header: "Source", cell: (e) => e.source.replace(/_/g, " ") },
            { key: "status", header: "Status", cell: (e) => (e.status_before !== e.status_after ? `${e.status_before ?? "—"} → ${e.status_after ?? "—"}` : (e.status_after ?? "—")) },
            { key: "who", header: "Reviewer", cell: (e) => <Mono>{e.actor_user_id ? shortId(e.actor_user_id) : "—"}</Mono> },
            { key: "reason", header: "Reason", cell: (e) => e.reason ?? "—" },
          ]}
        />
      </Card>
      <StatusCard entry={entry} />
      <ReinstateCard entry={entry} />
      <RevalidateCard entry={entry} />
    </div>
  );
}

function StatusCard({ entry }: { entry: ClientMatrixEntry }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [status, setStatus] = useState<SetClientMatrixStatusRequest["status"] | "">("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: SetClientMatrixStatusRequest, opts) => ep.staff.clientMatrix.setStatus(entry.approved_client_matrix_id, req, opts),
    () => refreshMatrix(qc),
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!status) return setFormError("Choose a status.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const ev = parseEvidence(evidence, { required: false });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    const req: SetClientMatrixStatusRequest = { status, reason: reason.trim() };
    if (Object.keys(ev.value).length) req.evidence = ev.value;
    if (status === "safety_suspended" && effectiveAt.trim()) req.effective_at = effectiveAt.trim();
    cmd.run(req);
  }
  return (
    <Card title="Suspend" subtitle="Suspending is single-reviewer and immediate, and strands in-flight engagement handles for this version. Reinstating needs a second reviewer — see below.">
      {cmd.data ? <CommandOutcome res={cmd.data} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Select
          label="New status"
          value={status}
          placeholder="Choose…"
          onChange={(e) => setStatus(e.target.value as SetClientMatrixStatusRequest["status"])}
          options={[
            { value: "ordinary_suspended", label: "Ordinary suspension" },
            { value: "safety_suspended", label: "Safety suspension (a full safety finding)" },
          ]}
        />
        {status === "safety_suspended" ? (
          <Input label="Effective-time boundary" optional mono value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} hint="RFC 3339. Empty means no known onset." />
        ) : null}
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <Textarea label="Evidence (JSON object)" optional className="mono" rows={2} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary" loading={cmd.isPending}>
            Change status
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ReinstateOutcome({ res }: { res: ReinstateClientMatrixResponse }) {
  if (res.result === "awaiting_second_approval") return <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} />;
  if (!res.applied) return <NotAppliedBanner result={res.result}>The entry is already validated.</NotAppliedBanner>;
  return (
    <Banner tone="success" live="polite" title="Recorded: reinstated">
      Status {res.status.replace(/_/g, " ")}
      {res.status_before ? ` (was ${res.status_before.replace(/_/g, " ")})` : ""}. A second, distinct reviewer's vote applied it.
    </Banner>
  );
}

/**
 * Migration 0069's dual-control reinstatement (POST .../reinstate,
 * request_client_matrix_reinstatement): any platform_reviewer may cast a
 * vote; a second, DISTINCT reviewer's vote is what actually reinstates.
 * Evidence is required on every vote, not just the one that applies it.
 * Mirrors Breaker.tsx's close action (the same §5 tighten/loosen shape),
 * including step-up MFA and idempotency via useStaffCommand — but unlike
 * the breaker, the listing doesn't expose votes cast so far, so this only
 * shows the outcome of the viewer's own call.
 */
function ReinstateCard({ entry }: { entry: ClientMatrixEntry }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: ReinstateClientMatrixRequest, opts) => ep.staff.clientMatrix.reinstate(entry.approved_client_matrix_id, req, opts),
    () => refreshMatrix(qc),
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const ev = parseEvidence(evidence, { required: true });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    cmd.run({ reason: reason.trim(), evidence: ev.value });
  }
  return (
    <Card
      title="Reinstate"
      subtitle="Dual-controlled: needs two distinct platform reviewers, keyed to this entry's current scope version. Nothing applies until a second, different reviewer also votes to reinstate."
    >
      {cmd.data ? <ReinstateOutcome res={cmd.data} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <Textarea
          label="Evidence (JSON object)"
          className="mono"
          rows={2}
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          hint='Required on every vote, e.g. {"phase0_report": "…"}.'
        />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary" loading={cmd.isPending}>
            Vote to reinstate
          </Button>
        </div>
      </form>
    </Card>
  );
}

function RevalidateCard({ entry }: { entry: ClientMatrixEntry }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [evidenceClass, setEvidenceClass] = useState<EvidenceClass | "">("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: RevalidateClientMatrixRequest, opts) => ep.staff.clientMatrix.revalidate(entry.approved_client_matrix_id, req, opts),
    () => refreshMatrix(qc),
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const ev = parseEvidence(evidence, { required: true });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    cmd.run({ reason: reason.trim(), evidence: ev.value, ...(evidenceClass ? { evidence_class: evidenceClass } : {}) });
  }
  return (
    <Card title="Record fresh validation" subtitle="Doesn't change the status.">
      {cmd.data ? <CommandOutcome res={cmd.data} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Select
          label="Evidence class"
          optional
          value={evidenceClass}
          onChange={(e) => setEvidenceClass(e.target.value as EvidenceClass | "")}
          options={[{ value: "", label: `Keep (${entry.evidence_class.replace(/_/g, " ")})` }, ...CLASS_OPTIONS]}
        />
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <Textarea label="Evidence (JSON object)" className="mono" rows={2} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary" loading={cmd.isPending}>
            Record validation
          </Button>
        </div>
      </form>
    </Card>
  );
}
